<script setup lang="ts">
import { useExplorerStore } from "@/modules/explorer/store";
import { computed, ref, watch } from "vue";

import CodeMirror from "vue-codemirror6";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { sql } from "@codemirror/lang-sql";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { convertDates } from "chartjs-graph";

const langSql = sql();
const langJson = json();
const lintJson = jsonParseLinter();

const clickhouseQueryIsVisible = defineModel<boolean>();
const tab = ref("graph-query");
const isLoadingData = ref(false);

const documentExport = computed(() => {
	return JSON.stringify(
		JSON.parse(JSON.stringify(useExplorerStore().document)),
		null,
		2
	);
});

const copyToClipboard = () => {
	let text = useExplorerStore().explorerState.lastClickhouseSql;

	if (tab.value == "base-data-query") {
		text = useExplorerStore().explorerState.lastClickhouseBaseDataSql;
	}
	if (tab.value == "document-json") {
		text = documentExport.value;
	}

	navigator.clipboard.writeText(text).then(() => {
		clickhouseQueryIsVisible.value = false;
	});
};

const isImportDialogVisible = ref(false);
const documentJsonImport = ref("");
const isJsonValid = ref(false);

const openImportDialog = () => {
	isImportDialogVisible.value = true;
	documentJsonImport.value = "";
};

const importDocument = async () => {
	let importedDocument = JSON.parse(documentJsonImport.value);
	importedDocument.parent_uuid = useExplorerStore().document.uuid;

	const response = await toastApiResult(
		useApiClient("explorer").validateDocument(importedDocument)
	);
	if (response.isOk()) {
		useExplorerStore().document = {
			...convertDates(importedDocument),
		};
		isImportDialogVisible.value = false;
		clickhouseQueryIsVisible.value = false;
	}
};

watch(documentJsonImport, (document: any) => {
	try {
		JSON.parse(document);
		isJsonValid.value = true;
	} catch (error) {
		isJsonValid.value = false;
	}
});
</script>

<template>
	<v-dialog v-model="isImportDialogVisible" width="70vw">
		<v-card elevation="9">
			<v-toolbar style="border-radius: 0 !important" flat>
				<v-toolbar-title
					><i class="fa-duotone fa-file-import fa-xl mr-3"></i>Import Document
					JSON</v-toolbar-title
				>
				<v-spacer></v-spacer>

				<v-btn icon @click="isImportDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider style="border-color: unset !important" />

			<perfect-scrollbar
				class="w-100"
				style="overflow-x: hidden; max-height: 40vh"
				:options="{ suppressScrollX: true }"
			>
				<code-mirror
					basic
					gutter
					:lang="langJson"
					:linter="lintJson"
					v-model="documentJsonImport"
				/>
			</perfect-scrollbar>

			<v-divider style="border-color: unset !important" />
			<v-card-actions class="pa-3">
				<v-spacer></v-spacer>
				<v-btn
					variant="flat"
					prepend-icon="mdi-import"
					:disabled="!isJsonValid"
					@click="importDocument"
					>Import</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-dialog v-model="clickhouseQueryIsVisible" width="90vw">
		<v-card elevation="9" :loading="isLoadingData" :disabled="isLoadingData">
			<v-toolbar density="comfortable" style="border-radius: 0 !important" flat>
				<v-toolbar-title>
					<i class="fa-duotone fa-brackets-curly fa-xl mr-3"></i>
					Developer dialog
				</v-toolbar-title>
				<v-spacer />

				<v-btn icon @click="clickhouseQueryIsVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>

				<template v-slot:extension>
					<v-tabs v-model="tab" style="min-height: 48px">
						<v-tab color="primary" value="graph-query"
							>Clickhouse Graph SQL</v-tab
						>
						<v-tab color="primary" value="base-data-query"
							>Clickhouse Base Data SQL</v-tab
						>
						<v-tab color="primary" value="document-json">Document JSON</v-tab>
					</v-tabs>
				</template>
			</v-toolbar>
			<template v-slot:loader="{ isActive }">
				<v-progress-linear
					:active="isActive"
					color="primary"
					height="4"
					indeterminate
				></v-progress-linear>
			</template>

			<v-divider style="border-color: unset !important" />

			<v-window v-model="tab">
				<v-window-item value="graph-query">
					<perfect-scrollbar
						class="w-100"
						style="overflow-x: hidden; max-height: 46vh"
						:options="{ suppressScrollX: true }"
					>
						<code-mirror
							basic
							:lang="langSql"
							v-model="useExplorerStore().explorerState.lastClickhouseSql"
						/>
					</perfect-scrollbar>
				</v-window-item>

				<v-window-item value="base-data-query">
					<perfect-scrollbar
						class="w-100"
						style="overflow-x: hidden; max-height: 46vh"
						:options="{ suppressScrollX: true }"
					>
						<code-mirror
							basic
							:lang="langSql"
							v-model="
								useExplorerStore().explorerState.lastClickhouseBaseDataSql
							"
						/>
					</perfect-scrollbar>
				</v-window-item>

				<v-window-item value="document-json">
					<perfect-scrollbar
						class="w-100"
						style="overflow-x: hidden; max-height: 46vh"
						:options="{ suppressScrollX: true }"
					>
						<code-mirror
							basic
							gutter
							readonly
							:lang="langJson"
							:linter="lintJson"
							v-model="documentExport"
						/>
					</perfect-scrollbar>
				</v-window-item>
			</v-window>
			<v-divider style="border-color: unset !important" />
			<v-card-actions class="pa-3">
				<v-spacer></v-spacer>
				<v-fade-transition>
					<v-btn
						variant="flat"
						v-if="tab === 'document-json'"
						prepend-icon="mdi-import"
						@click="openImportDialog"
						>Import Document JSON</v-btn
					>
				</v-fade-transition>
				<v-btn
					variant="flat"
					prepend-icon="mdi-content-copy"
					@click="copyToClipboard()"
					>Copy to clipboard & Close</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style lang="scss">
.cm-wrap {
	height: 100%;
}
.cm-scroller {
	overflow: auto;
}
</style>
